import { useState } from "react";
import { trpc } from "../lib/trpc";
import {
  Wrench, Globe, Terminal, Image, Link2, Play, RefreshCw,
  Code, FileText, Clock, CheckCircle2, XCircle, ExternalLink,
  Copy, ChevronDown, ChevronRight, Zap, Hash
} from "lucide-react";

type Tab = "commands" | "browser" | "process" | "media" | "links";

export function Tools() {
  const [activeTab, setActiveTab] = useState<Tab>("commands");

  // Commands
  const [cmdInput, setCmdInput] = useState("");
  const { data: commands } = trpc.tools.listCommands.useQuery();
  const { data: cmdHistory } = trpc.tools.listCommandHistory.useQuery({ limit: 20 });
  const execCmdMut = trpc.tools.executeCommand.useMutation();

  // Browser
  const [browserUrl, setBrowserUrl] = useState("");
  const browserExtractMut = trpc.tools.browserExtract.useMutation();

  // Process
  const [processRuntime, setProcessRuntime] = useState<"node" | "python" | "shell">("node");
  const [processCode, setProcessCode] = useState("");
  const { data: processHistory } = trpc.tools.listProcessExecutions.useQuery({ limit: 10 });
  const execProcessMut = trpc.tools.executeProcess.useMutation();

  // Media
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaType, setMediaType] = useState<"image" | "audio" | "document">("image");
  const { data: mediaAssets } = trpc.tools.listMediaAssets.useQuery({ limit: 20 });
  const processMediaMut = trpc.tools.processMedia.useMutation();

  // Links
  const [linkUrl, setLinkUrl] = useState("");
  const { data: enrichedLinks } = trpc.tools.listEnrichedLinks.useQuery({ limit: 20 });
  const enrichLinkMut = trpc.tools.enrichLink.useMutation();

  const utils = trpc.useUtils();

  function handleCommand() {
    if (!cmdInput.trim()) return;
    const parts = cmdInput.trim().split(" ");
    const command = parts[0].startsWith("/") ? parts[0].slice(1) : parts[0];
    const args = parts.slice(1).join(" ");
    execCmdMut.mutate({ text: cmdInput.trim() }, {
      onSuccess: () => { utils.tools.listCommandHistory.invalidate(); setCmdInput(""); },
    });
  }

  const tabs: { id: Tab; label: string; icon: typeof Wrench }[] = [
    { id: "commands", label: "Comandos", icon: Hash },
    { id: "browser", label: "Browser", icon: Globe },
    { id: "process", label: "Process", icon: Terminal },
    { id: "media", label: "Media", icon: Image },
    { id: "links", label: "Links", icon: Link2 },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Wrench className="w-7 h-7 text-success" />
          Tools
        </h1>
        <p className="text-sm text-gray-400 mt-1">
          Ferramentas avançadas do YOS: comandos, browser, scripts, media e links
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-900 p-1 rounded-lg w-fit flex-wrap">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? "bg-gray-700 text-white"
                : "text-gray-400 hover:text-white"
            }`}
          >
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      {/* Commands Tab */}
      {activeTab === "commands" && (
        <div className="space-y-4">
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <div className="flex gap-2">
              <input
                value={cmdInput}
                onChange={(e) => setCmdInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCommand()}
                placeholder="/help, /status, /agents, /search query..."
                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white font-mono"
              />
              <button
                onClick={handleCommand}
                disabled={execCmdMut.isPending}
                className="px-4 py-2 bg-primary hover:bg-primary/80 text-white rounded-lg flex items-center gap-2"
              >
                {execCmdMut.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Executar
              </button>
            </div>
            {(execCmdMut as any).data && (
              <pre className="mt-3 p-3 bg-gray-800 rounded-lg text-sm text-success font-mono overflow-x-auto whitespace-pre-wrap">
                {typeof (execCmdMut as any).data === "string" ? (execCmdMut as any).data : JSON.stringify((execCmdMut as any).data, null, 2)}
              </pre>
            )}
          </div>

          {/* Available Commands */}
          {commands && commands.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-400 mb-2">Comandos Disponíveis</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {commands.map((cmd: any) => (
                  <div key={cmd.name} className="bg-gray-900 border border-gray-800 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <code className="text-success text-sm">/{cmd.name}</code>
                      <p className="text-xs text-gray-400 mt-0.5">{cmd.description}</p>
                    </div>
                    <button
                      onClick={() => setCmdInput(`/${cmd.name} `)}
                      className="p-1 hover:bg-gray-800 rounded"
                    >
                      <Play className="w-3 h-3 text-gray-400" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Command History */}
          {cmdHistory && cmdHistory.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-400 mb-2">Histórico</h3>
              <div className="space-y-1">
                {cmdHistory.map((h: any) => (
                  <div key={h.id} className="bg-gray-900 border border-gray-800 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {h.success ? <CheckCircle2 className="w-3 h-3 text-success" /> : <XCircle className="w-3 h-3 text-red-400" />}
                      <code className="text-sm text-white">/{h.command} {h.args || ""}</code>
                    </div>
                    <span className="text-xs text-gray-500">{h.duration}ms</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Browser Tab */}
      {activeTab === "browser" && (
        <div className="space-y-4">
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <label className="block text-sm text-gray-400 mb-1">URL para extrair conteúdo</label>
            <div className="flex gap-2">
              <input
                value={browserUrl}
                onChange={(e) => setBrowserUrl(e.target.value)}
                placeholder="https://example.com"
                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              />
              <button
                onClick={() => browserExtractMut.mutate({ url: browserUrl })}
                disabled={browserExtractMut.isPending || !browserUrl}
                className="px-4 py-2 bg-primary hover:bg-primary/80 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                {browserExtractMut.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
                Extrair
              </button>
            </div>
            {(browserExtractMut as any).data && (
              <pre className="mt-3 p-3 bg-gray-800 rounded-lg text-sm text-white font-mono overflow-x-auto max-h-96 whitespace-pre-wrap">
                {typeof (browserExtractMut as any).data === "string" ? (browserExtractMut as any).data : JSON.stringify((browserExtractMut as any).data, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}

      {/* Process Tab */}
      {activeTab === "process" && (
        <div className="space-y-4">
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <div className="flex gap-2 mb-3">
              {(["node", "python", "shell"] as const).map((rt) => (
                <button
                  key={rt}
                  onClick={() => setProcessRuntime(rt)}
                  className={`px-3 py-1.5 rounded-lg text-sm ${
                    processRuntime === rt ? "bg-primary text-white" : "bg-gray-800 text-gray-400 hover:text-white"
                  }`}
                >
                  {rt === "node" ? "Node.js" : rt === "python" ? "Python" : "Shell"}
                </button>
              ))}
            </div>
            <textarea
              value={processCode}
              onChange={(e) => setProcessCode(e.target.value)}
              placeholder={processRuntime === "node" ? 'console.log("Hello from Node.js");' : processRuntime === "python" ? 'print("Hello from Python")' : 'echo "Hello from Shell"'}
              rows={6}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white font-mono text-sm"
            />
            <button
              onClick={() => execProcessMut.mutate({ runtime: processRuntime, code: processCode }, {
                onSuccess: () => utils.tools.listProcessExecutions.invalidate(),
              })}
              disabled={execProcessMut.isPending || !processCode}
              className="mt-2 px-4 py-2 bg-primary hover:bg-primary/80 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              {execProcessMut.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Executar
            </button>
            {execProcessMut.data && (
              <div className="mt-3 space-y-2">
                {execProcessMut.data.stdout && (
                  <div>
                    <div className="text-xs text-gray-400 mb-1">stdout:</div>
                    <pre className="p-3 bg-gray-800 rounded-lg text-sm text-success font-mono overflow-x-auto whitespace-pre-wrap">{execProcessMut.data.stdout}</pre>
                  </div>
                )}
                {execProcessMut.data.stderr && (
                  <div>
                    <div className="text-xs text-gray-400 mb-1">stderr:</div>
                    <pre className="p-3 bg-gray-800 rounded-lg text-sm text-red-400 font-mono overflow-x-auto whitespace-pre-wrap">{execProcessMut.data.stderr}</pre>
                  </div>
                )}
                <div className="text-xs text-gray-500">
                  Exit code: {execProcessMut.data.exitCode} | Duration: {execProcessMut.data.duration}ms
                </div>
              </div>
            )}
          </div>

          {/* Process History */}
          {processHistory && processHistory.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-400 mb-2">Histórico de Execuções</h3>
              <div className="space-y-2">
                {processHistory.map((exec: any) => (
                  <div key={exec.id} className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        {exec.status === "completed" ? <CheckCircle2 className="w-3 h-3 text-success" /> : <XCircle className="w-3 h-3 text-red-400" />}
                        <span className="text-sm text-white">{exec.runtime}</span>
                        <span className="text-xs text-gray-500">{exec.duration}ms</span>
                      </div>
                      <span className="text-xs text-gray-500">{new Date(exec.createdAt).toLocaleString("pt-BR")}</span>
                    </div>
                    <pre className="text-xs text-gray-400 font-mono truncate">{exec.code?.slice(0, 100)}</pre>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Media Tab */}
      {activeTab === "media" && (
        <div className="space-y-4">
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <div className="flex gap-2 mb-3">
              {(["image", "audio", "document"] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setMediaType(t)}
                  className={`px-3 py-1.5 rounded-lg text-sm ${
                    mediaType === t ? "bg-primary text-white" : "bg-gray-800 text-gray-400 hover:text-white"
                  }`}
                >
                  {t === "image" ? "Imagem" : t === "audio" ? "Áudio" : "Documento"}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={mediaUrl}
                onChange={(e) => setMediaUrl(e.target.value)}
                placeholder="https://example.com/file.png"
                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              />
              <button
                onClick={() => processMediaMut.mutate({ type: mediaType, url: mediaUrl }, {
                  onSuccess: () => utils.tools.listMediaAssets.invalidate(),
                })}
                disabled={processMediaMut.isPending || !mediaUrl}
                className="px-4 py-2 bg-primary hover:bg-primary/80 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                {processMediaMut.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                Processar
              </button>
            </div>
            {processMediaMut.data && (
              <div className="mt-3 p-3 bg-gray-800 rounded-lg">
                <div className="text-xs text-gray-400 mb-1">Conteúdo extraído:</div>
                <p className="text-sm text-white whitespace-pre-wrap">{processMediaMut.data.content}</p>
              </div>
            )}
          </div>

          {/* Media Assets */}
          {mediaAssets && mediaAssets.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-400 mb-2">Mídias Processadas</h3>
              <div className="space-y-2">
                {mediaAssets.map((asset: any) => (
                  <div key={asset.id} className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Image className="w-4 h-4 text-success" />
                        <span className="text-sm text-white">{asset.fileName || asset.type}</span>
                        <span className="text-xs text-gray-500">{asset.type}</span>
                      </div>
                      <span className="text-xs text-gray-500">{new Date(asset.createdAt).toLocaleString("pt-BR")}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1 truncate">{asset.processedContent?.slice(0, 150)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Links Tab */}
      {activeTab === "links" && (
        <div className="space-y-4">
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <div className="flex gap-2">
              <input
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com/article"
                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              />
              <button
                onClick={() => enrichLinkMut.mutate({ url: linkUrl }, {
                  onSuccess: () => { utils.tools.listEnrichedLinks.invalidate(); setLinkUrl(""); },
                })}
                disabled={enrichLinkMut.isPending || !linkUrl}
                className="px-4 py-2 bg-primary hover:bg-primary/80 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                {enrichLinkMut.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                Enriquecer
              </button>
            </div>
            {enrichLinkMut.data && (
              <div className="mt-3 p-3 bg-gray-800 rounded-lg space-y-2">
                <div className="text-white font-medium">{enrichLinkMut.data.title}</div>
                <p className="text-sm text-gray-400">{enrichLinkMut.data.description}</p>
                {enrichLinkMut.data.siteName && (
                  <span className="text-xs text-gray-500">{enrichLinkMut.data.siteName}</span>
                )}
                {enrichLinkMut.data.content && (
                  <div className="mt-2">
                    <div className="text-xs text-muted-foreground mb-1">Conteúdo extraído:</div>
                    <p className="text-xs text-foreground/70 max-h-40 overflow-y-auto whitespace-pre-wrap">{enrichLinkMut.data.content.slice(0, 500)}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Enriched Links */}
          {enrichedLinks && enrichedLinks.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-400 mb-2">Links Enriquecidos</h3>
              <div className="space-y-2">
                {enrichedLinks.map((link: any) => (
                  <div key={link.id} className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-white font-medium">{link.title || link.url}</div>
                        <p className="text-xs text-gray-400 mt-0.5">{link.description?.slice(0, 100)}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500">{link.type}</span>
                          {link.siteName && <span className="text-xs text-gray-500">{link.siteName}</span>}
                        </div>
                      </div>
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="p-1 hover:bg-gray-800 rounded">
                        <ExternalLink className="w-4 h-4 text-gray-400" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
